"use client";

import { useState } from "react";
import Link from "next/link";
import { useRouter } from "next/navigation";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { Scale, Eye, EyeOff, LogIn } from "lucide-react";
import { GlassCard } from "@/components/design-system/GlassCard";
import { CyberButton } from "@/components/design-system/CyberButton";
import { HolographicText } from "@/components/design-system/HolographicText";
import { useAuth } from "@/hooks/use-auth";
import { useAuthStore } from "@/stores/auth";
import { useNotificationStore } from "@/stores/notifications";

const loginSchema = z.object({
  email: z.string().email("Enter a valid email address"),
  password: z.string().min(8, "Password must be at least 8 characters"),
  barNumber: z.string().optional(),
});

type LoginFormData = z.infer<typeof loginSchema>;

export default function LoginPage() {
  const router = useRouter();
  const addNotification = useNotificationStore((s) => s.addNotification);
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const { login, isLoading } = useAuth();

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<LoginFormData>({
    resolver: zodResolver(loginSchema),
  });

  const onSubmit = async (data: LoginFormData) => {
    setError(null);

    try {
      await login(data.email, data.password);
      addNotification({ type: "analysis_complete", title: "Welcome Back", message: "You have been signed in successfully." });
      router.push("/cases");
    } catch (err) {
      const message =
        err instanceof Error ? err.message : "Invalid credentials. Please try again.";
      setError(message);
      addNotification({ type: "raid_alert", title: "Sign In Failed", message });
    }
  };

  const [demoLoading, setDemoLoading] = useState(false);

  const handleDemoLogin = async () => {
    setDemoLoading(true);
    setError(null);

    const demoEmail = "demo@framecounsel.com";
    const demoPassword = "DemoPass123!";

    try {
      // Try logging in with the demo account first
      await login(demoEmail, demoPassword);
      addNotification({ type: "analysis_complete", title: "Demo Mode", message: "Welcome to FrameCounsel! Explore with full functionality." });
      router.push("/cases");
    } catch {
      // If login fails, the demo account probably doesn't exist yet — register it
      try {
        const { register: registerUser } = useAuthStore.getState();
        await registerUser({
          organization_name: "Demo Law Firm",
          email: demoEmail,
          password: demoPassword,
          first_name: "Demo",
          last_name: "Attorney",
          bar_number: "12345",
          bar_state: "CA",
          app_role: "defense_attorney",
        });
        addNotification({ type: "analysis_complete", title: "Demo Mode", message: "Demo account created. Welcome to FrameCounsel!" });
        router.push("/cases");
      } catch (regErr) {
        // Registration also failed — maybe the account exists but password is wrong,
        // or the API is unreachable. Fall back to offline demo mode.
        const { setUser, setTokens } = useAuthStore.getState();
        setTokens("demo-token-for-preview", "demo-refresh-token");
        setUser({
          id: "demo-user-001",
          email: demoEmail,
          first_name: "Demo",
          last_name: "Attorney",
          app_role: "admin",
          team_role: "lead_counsel",
          bar_number: "12345",
          bar_state: "CA",
          tenant_id: "demo-tenant",
          requires_2fa: false,
        });
        addNotification({ type: "analysis_complete", title: "Demo Mode (Offline)", message: "API unavailable — exploring UI only. Some features won't load data." });
        router.push("/cases");
      }
    } finally {
      setDemoLoading(false);
    }
  };

  return (
    <div className="space-y-6">
      {/* Logo & Title */}
      <div className="text-center space-y-3">
        <div className="inline-flex items-center justify-center w-16 h-16 rounded-2xl bg-neon-cyan/10 border border-neon-cyan/30 mb-2">
          <Scale className="w-8 h-8 text-neon-cyan" />
        </div>
        <HolographicText as="h1" className="text-3xl block">
          FrameCounsel
        </HolographicText>
        <p className="text-white/50 text-sm">
          Forensic Video Evidence Platform
        </p>
      </div>

      {/* Login Card */}
      <GlassCard variant="strong" padding="lg" neonBorder>
        <form onSubmit={handleSubmit(onSubmit)} className="space-y-5">
          <div className="text-center mb-6">
            <h2 className="text-lg font-[family-name:var(--font-space-grotesk)] font-semibold text-white/90">
              Sign In
            </h2>
            <p className="text-xs text-white/40 mt-1">
              Access your case files and evidence
            </p>
          </div>

          {error && (
            <div role="alert" className="p-3 rounded-lg bg-neon-red/10 border border-neon-red/30 text-neon-red text-sm">
              {error}
            </div>
          )}

          {/* Email */}
          <div className="space-y-1.5">
            <label
              htmlFor="email"
              className="block text-xs font-mono uppercase tracking-wider text-white/50"
            >
              Email Address
            </label>
            <input
              id="email"
              type="email"
              autoComplete="email"
              placeholder="counsel@lawfirm.com"
              className="w-full px-4 py-3 rounded-lg bg-white/5 border border-white/10 text-white placeholder:text-white/20 focus:outline-none focus:border-neon-cyan/50 focus:bg-white/[0.07] transition-all duration-200 text-sm"
              {...register("email")}
            />
            {errors.email && (
              <p role="alert" className="text-neon-red text-xs mt-1">{errors.email.message}</p>
            )}
          </div>

          {/* Password */}
          <div className="space-y-1.5">
            <label
              htmlFor="password"
              className="block text-xs font-mono uppercase tracking-wider text-white/50"
            >
              Password
            </label>
            <div className="relative">
              <input
                id="password"
                type={showPassword ? "text" : "password"}
                autoComplete="current-password"
                placeholder="Enter your password"
                className="w-full px-4 py-3 pr-11 rounded-lg bg-white/5 border border-white/10 text-white placeholder:text-white/20 focus:outline-none focus:border-neon-cyan/50 focus:bg-white/[0.07] transition-all duration-200 text-sm"
                {...register("password")}
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-white/30 hover:text-white/60 transition-colors"
                aria-label={showPassword ? "Hide password" : "Show password"}
              >
                {showPassword ? (
                  <EyeOff className="w-4 h-4" />
                ) : (
                  <Eye className="w-4 h-4" />
                )}
              </button>
            </div>
            {errors.password && (
              <p role="alert" className="text-neon-red text-xs mt-1">{errors.password.message}</p>
            )}
          </div>

          {/* Bar Number (Optional) */}
          <div className="space-y-1.5">
            <label
              htmlFor="barNumber"
              className="block text-xs font-mono uppercase tracking-wider text-white/50"
            >
              Bar Number{" "}
              <span className="text-white/20">(Optional)</span>
            </label>
            <input
              id="barNumber"
              type="text"
              placeholder="e.g. 12345"
              className="w-full px-4 py-3 rounded-lg bg-white/5 border border-white/10 text-white placeholder:text-white/20 focus:outline-none focus:border-neon-cyan/50 focus:bg-white/[0.07] transition-all duration-200 text-sm"
              {...register("barNumber")}
            />
          </div>

          {/* Submit */}
          <CyberButton
            type="submit"
            color="cyan"
            fullWidth
            loading={isLoading}
            icon={<LogIn className="w-4 h-4" />}
          >
            Sign In
          </CyberButton>

          {/* Links */}
          <div className="flex items-center justify-between text-xs">
            <Link
              href="/register"
              className="text-neon-cyan/70 hover:text-neon-cyan transition-colors"
            >
              Create Account
            </Link>
            <Link
              href="/forgot-password"
              className="text-white/30 hover:text-white/60 transition-colors"
            >
              Forgot Password?
            </Link>
          </div>
        </form>
      </GlassCard>

      {/* Demo Access */}
      <GlassCard padding="md" className="border-neon-purple/20">
        <div className="text-center space-y-3">
          <p className="text-xs text-white/40">No backend? Explore the UI with demo mode.</p>
          <CyberButton
            type="button"
            color="purple"
            fullWidth
            onClick={handleDemoLogin}
            loading={demoLoading}
            icon={<Scale className="w-4 h-4" />}
          >
            Enter Demo Mode
          </CyberButton>
        </div>
      </GlassCard>

      {/* Footer */}
      <p className="text-center text-[11px] text-white/20">
        Protected by attorney-client privilege encryption.
        <br />
        All evidence handled in compliance with FRE standards.
      </p>
    </div>
  );
}
